import '@material/web/select/outlined-select.js';
import '@material/web/select/select-option.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/chips/chip-set.js';
import '@material/web/chips/filter-chip.js';

import { api, type ClosedTask, type Member, type Tag, type Task, type Template } from '../api.js';
import { ce, el, clear, fmtMinutes, type ChipEl, type ValueEl } from '../dom.js';
import { getLocale, t, type MessageKey } from '../i18n.js';
import { icons } from '../icons.js';
import {
  taskComposer,
  eligibilityPicker,
  type ComposedTask,
  type ComposedFollowup,
  type EligibilityOption,
  type EligibilityRefsData,
} from './authoring.js';
import { field } from '../components/field.js';
import { fab } from '../components/fab.js';
import { sheet } from '../components/sheet.js';

// A collapsible builder section (UI-16): summary row (label + current value +
// chevron) that expands to its controls. Native <details>, styled — the
// progressive-disclosure pattern the harness prototype established.
function builderGroup(label: string, open: boolean, ...body: (Node | null)[]): HTMLDetailsElement {
  const d = el('details', { class: 'builder-group' }) as HTMLDetailsElement;
  if (open) d.setAttribute('open', '');
  d.append(
    el('summary', {}, el('span', { class: 'grp-label' }, label), el('span', { class: 'grp-chev' }, icons.chevronDown())),
    el('div', { class: 'grp-body' }, ...body),
  );
  return d;
}

// A labelled control block inside a builder section.
function fieldBlock(label: string, control: Node): HTMLElement {
  return el('div', { class: 'composer-field' }, el('label', {}, label), control);
}

// (Routines folded into the unified task model — ADR-0040 phase 3. The old
// routineCard and its /api/routine calls were removed; former routine items
// are now ordinary daily, member-targeted tasks in the list below.)

export interface TaskRowHandlers {
  onComplete: (task: Task, button: HTMLButtonElement) => void;
  onSkip: (task: Task, button: HTMLButtonElement) => void;
  // Admin-only "cancel" (server-enforced, ADR pending — see PENDING.md).
  // Omit to hide the "won't do" button entirely.
  onCancel?: (task: Task, button: HTMLButtonElement) => void;
  // Opens the "more options" menu — currently just complete-as-another-
  // member (see renderTaskMenu); won't-do/not-needed stay on the row as
  // their own buttons. Omit to hide the "more" button entirely.
  onMore?: (task: Task, button: HTMLButtonElement) => void;
}

// A family member eligible to have a task completed on their behalf via
// the "complete as…" section of the task menu.
export interface TaskMenuMember {
  id: number;
  name: string;
}

export interface TaskMenuOptions {
  // Members eligible for this task. Omit/empty to hide the whole
  // "complete as…" section (e.g. production has no backend for this yet —
  // see PENDING.md).
  members?: TaskMenuMember[];
  onCompleteAs?: (member: TaskMenuMember) => void;
  // Won't-do / not-needed already have their own row buttons (see
  // taskRow), so these are optional extras only for surfaces that don't
  // show those buttons on the row (e.g. a denser layout in future).
  onCancel?: () => void;
  onSkip?: () => void;
}

function iconButton(
  cls: string,
  label: string,
  hint: string,
  iconNode: HTMLElement,
  onclick: (ev: Event) => void,
): HTMLElement {
  return el(
    'button',
    { class: `icon-btn ${cls}`, type: 'button', 'aria-label': label, title: hint, onclick },
    iconNode,
  );
}

function menuItem(iconNode: HTMLElement, label: string, onclick: () => void): HTMLElement {
  return el('button', { class: 'menu-item', type: 'button', onclick }, iconNode, el('span', {}, label));
}

// The "more options" menu content: complete-as-another-member (if any are
// eligible), then — only when there's something to divide it from — a
// divider, then won't-do / not-needed (only used when a caller doesn't
// already show those as row buttons; taskRow's default menu invocation
// leaves them out since they're on the row). Returns just the content —
// callers place it in whatever surface they open (a <dialog> in the real
// app, a static example in the component harness).
export function renderTaskMenu(task: Task, opts: TaskMenuOptions): HTMLElement {
  const menu = el('div', { class: 'task-menu' });
  menu.append(el('h3', { class: 'task-menu-title' }, task.title));
  const hasMembers = !!opts.members && opts.members.length > 0 && !!opts.onCompleteAs;
  const hasTrailingItems = !!opts.onCancel || !!opts.onSkip;
  if (hasMembers) {
    const onCompleteAs = opts.onCompleteAs!;
    menu.append(el('p', { class: 'muted task-menu-section-label' }, t('tasksCompleteFor')));
    for (const member of opts.members!) {
      menu.append(el('button', { class: 'menu-item', type: 'button', onclick: () => onCompleteAs(member) }, member.name));
    }
    if (hasTrailingItems) menu.append(el('hr', { class: 'menu-divider' }));
  }
  if (opts.onCancel) menu.append(menuItem(icons.wontDo(), t('tasksWontDo'), opts.onCancel));
  if (opts.onSkip) menu.append(menuItem(icons.notNeeded(), t('tasksNotNeeded'), opts.onSkip));
  return menu;
}

// The single task-list row: title, payout (with boost pill), due date, and
// up to 4 icon actions (won't-do, not-needed, done, more — "more" opens a
// menu of extra actions, currently just complete-as-another-member, see
// renderTaskMenu). Pulled out as its own function so it can be reused by
// the visual harness (docs/decisions/0035-component-harness.md) without
// dragging in the whole tasks view's data loading. Icon buttons are
// 44x44 CSS px (WCAG 2.5.8 / platform touch-target minimum, ADR-0036) and
// carry aria-label since they have no visible text.
export function taskRow(task: Task, handlers: TaskRowHandlers): HTMLElement {
  const boosted = task.currentBoostMultiplier > 1;
  const actions = el('div', { class: 'task-actions' });
  if (handlers.onCancel) {
    const cancel = handlers.onCancel;
    actions.append(
      iconButton('', t('tasksWontDo'), t('tasksWontDoHint'), icons.wontDo(), (ev) =>
        cancel(task, ev.currentTarget as HTMLButtonElement),
      ),
    );
  }
  actions.append(
    iconButton('', t('tasksNotNeeded'), t('tasksNotNeededHint'), icons.notNeeded(), (ev) =>
      handlers.onSkip(task, ev.currentTarget as HTMLButtonElement),
    ),
    iconButton('primary', t('tasksDone'), t('tasksDone'), icons.done(), (ev) =>
      handlers.onComplete(task, ev.currentTarget as HTMLButtonElement),
    ),
  );
  if (handlers.onMore) {
    const more = handlers.onMore;
    actions.append(
      iconButton('', t('tasksMore'), t('tasksMoreHint'), icons.more(), (ev) =>
        more(task, ev.currentTarget as HTMLButtonElement),
      ),
    );
  }

  return el(
    'div',
    { class: 'task', 'data-task': task.title },
    el(
      'div',
      { class: 'task-body' },
      el('div', { class: 'title' }, task.title),
      el(
        'div',
        { class: 'meta' },
        el('span', {}, fmtMinutes(task.currentPayoutMinutes)),
        boosted ? el('span', { class: 'pill boost' }, `↑ ${task.currentBoostMultiplier.toFixed(1)}×`) : null,
        task.dueAt ? el('span', {}, t('tasksDue', { date: new Date(task.dueAt).toLocaleDateString(getLocale()) })) : null,
        // Missed (ADR-0040 phase 1): flagged, but still shown among open
        // tasks and still completable — not a terminal status.
        task.missedAt ? el('span', { class: 'pill missed' }, t('tasksMissedLabel')) : null,
      ),
    ),
    actions,
  );
}

// The "nothing to do" state. Deliberately NOT inside a card: framed in the
// same border, padding and radius as a task row, "Nothing to do" reads as a
// task named "nothing to do" (ADR-0047). It sits directly on the page.
// Locale-agnostic (label as data) so the harness can render it.
export function taskListEmpty(label: string): HTMLElement {
  return el('p', { class: 'task-empty muted' }, label);
}

export function renderTasks(): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const list = el('div', { class: 'card', id: 'task-list' });

  function load(): void {
    api
      .get<{ tasks: Task[] }>('/api/tasks')
      .then(({ tasks }) => {
        clear(list);
        // The card chrome belongs to the LIST, not to the screen: with no
        // tasks there is no list to frame (ADR-0047).
        list.classList.toggle('card', tasks.length > 0);
        if (tasks.length === 0) {
          list.append(taskListEmpty(t('tasksEmpty')));
          return;
        }
        for (const task of tasks) {
          list.append(
            taskRow(task, {
              onSkip: (tk, button) => {
                button.disabled = true;
                api
                  .post(`/api/tasks/${tk.id}/skip`)
                  .then(() => {
                    load();
                    loadHistory();
                  })
                  .catch((err: Error) => {
                    error.textContent = err.message;
                    load();
                  });
              },
              onComplete: (tk, button) => {
                button.disabled = true;
                api
                  .post(`/api/tasks/${tk.id}/complete`)
                  .then(() => {
                    load();
                    loadHistory();
                  })
                  .catch((err: Error) => {
                    error.textContent = err.message;
                    load();
                  });
              },
            }),
          );
        }
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  // Add-task composer (ADR-0042), now inside the quick-add sheet behind the
  // FAB (ADR-0047): name + worth, "More" reveals eligibility/visibility/
  // follow-ups + "Save as template", and typing a saved template's name
  // autocompletes "create task from template". Rebuilt after each successful
  // action so the form clears and the template list refreshes.
  const quickAdd = sheet({
    id: 'quick-add',
    title: t('tasksAddTitle'),
    closeLabel: t('close'),
    variant: 'bottom',
  });
  // Failures render HERE, inside the surface that caused them; successes
  // render in the page-level `error` line on the list (ADR-0047).
  const sheetError = el('p', { class: 'error' });

  // Options shared by the quick-add composer and the full builder, refreshed
  // by loadComposer(). The builder is opened on demand and reads these.
  let eligibilityOptions: EligibilityOption[] = [];
  let categoryTags: { id: number; name: string }[] = [];
  let templateOptions: { id: number; title: string; baseMinutes: number }[] = [];

  function reloadAll(): void {
    load();
    loadHistory();
    loadComposer();
  }

  /**
   * The ADR-0047 invariant, in one place:
   *
   *   a FAILURE keeps the surface open and renders inside it;
   *   a SUCCESS closes the surface and renders on the page.
   *
   * "Created, pending approval" is a success — so the sheet closes and the
   * message lands on the list, where the user is now looking. It used to be
   * written into an element that, once authoring moved into a sheet, would
   * have sat behind the sheet the user was still staring at.
   */
  function postTask(body: Record<string, unknown>): Promise<void> {
    error.textContent = '';
    sheetError.textContent = '';
    return api
      .post<{ pending?: boolean }>('/api/tasks', body)
      .then((res) => {
        quickAdd.close();
        if (res.pending) error.textContent = t('sentForApproval');
        reloadAll();
      })
      .catch((err: Error) => {
        sheetError.textContent = err.message;
      });
  }

  function composedToBody(c: ComposedTask): Record<string, unknown> {
    const body: Record<string, unknown> = { title: c.title, baseMinutes: c.baseMinutes };
    if (c.eligibilityRefs.memberIds.length > 0 || c.eligibilityRefs.tagIds.length > 0) {
      body.eligibilityRefs = c.eligibilityRefs;
    }
    if (c.visibility !== 'everyone') body.visibility = c.visibility;
    return body;
  }

  function loadComposer(): void {
    Promise.all([
      api.get<{ templates: Template[] }>('/api/templates'),
      api.get<{ members?: Member[] }>('/api/state'),
      api.get<{ tags: Tag[] }>('/api/tags'),
    ])
      .then(([{ templates }, { members }, { tags }]) => {
        eligibilityOptions = [
          ...tags
            .filter((x) => x.kind === 'eligibility')
            .map((g) => ({ kind: 'group' as const, id: g.id, name: g.name })),
          ...(members ?? []).map((m) => ({ kind: 'member' as const, id: m.id, name: m.name, color: m.color })),
        ];
        categoryTags = tags.filter((x) => x.kind === 'category').map((x) => ({ id: x.id, name: x.name }));
        const tplOptions = templates
          .filter((tp) => tp.active)
          .map((tp) => ({ id: tp.id, title: tp.title, baseMinutes: tp.baseMinutes }));
        templateOptions = tplOptions;

        clear(quickAdd.body);
        quickAdd.body.append(
          sheetError,
          taskComposer({
            templates: tplOptions,
            eligibilityOptions,
            nameInputId: 'new-task-title',
            worthInputId: 'new-task-minutes',
            asForm: true,
            // "More options" now opens the full builder (UI-16), carrying the
            // name and worth already typed (ADR-0047 — no lost input).
            onMore: (c) => openBuilder({ title: c.title, minutes: c.baseMinutes }),
            labels: {
              nameLabel: t('composeNameLabel'),
              placeholder: t('tasksAddPlaceholder'),
              worth: t('composeWorth'),
              minSuffix: t('composeMinSuffix'),
              add: t('tasksAdd'),
              more: t('composeMore'),
              less: t('composeLess'),
              fromTemplate: t('composeFromTemplate'),
              whoCanDo: t('composeWhoCanDo'),
              visibility: t('composeVisibility'),
              visEveryone: t('tasksVisibilityEveryone'),
              visAssignee: t('tasksVisibilityAssignee'),
              visAdmins: t('tasksVisibilityAdmins'),
              followupHeading: t('composeFollowupHeading'),
              followupAfter: t('composeFollowupAfter'),
              followupTemplatePlaceholder: t('composeFollowupTemplate'),
              followupAdd: t('composeFollowupAdd'),
              followupRemove: t('composeFollowupRemove'),
              addTask: t('composeAddTask'),
              saveAsTemplate: t('composeSaveTemplate'),
              templateHint: t('composeTemplateHint'),
              elig: {
                everyone: t('eligEveryone'),
                typePlaceholder: t('eligType'),
                groupTag: t('eligGroupTag'),
                noMatches: t('eligNoMatches'),
                remove: t('eligRemove'),
              },
            },
            onAdd: (c) => {
              // Validation failure: the surface stays open with the input
              // intact, message inside it (ADR-0047).
              if (!c.title || !c.baseMinutes) {
                sheetError.textContent = t('tasksNameMinutesRequired');
                return;
              }
              void postTask(composedToBody(c));
            },
            onCreateFromTemplate: (id) => {
              sheetError.textContent = '';
              api
                .post(`/api/templates/${id}/spawn`)
                .then(() => {
                  // Spawning from a template is a success like any other.
                  quickAdd.close();
                  reloadAll();
                })
                .catch((err: Error) => { sheetError.textContent = err.message; });
            },
          }),
        );
      })
      .catch((err: Error) => { error.textContent = err.message; });
  }

  // The full task builder (UI-16): one full-height sheet that absorbs the
  // composer's More panel (who/visibility/follow-ups/save-as-template) and
  // the old Advanced modal (due date, labels, timed show/hide) into sectioned,
  // progressively-disclosed groups. Recurrence is NOT here — a repeating thing
  // is a template, so recurrence is set on templates (admin editor). Built
  // once; openBuilder() repopulates it, carrying name/worth from the sheet.
  const builder = sheet({ id: 'task-builder', title: t('builderTitle'), closeLabel: t('close'), variant: 'full' });
  const builderError = el('p', { class: 'error' });

  function openBuilder(prefill: { title: string; minutes: number }): void {
    builderError.textContent = '';

    const nameF = field({ id: 'builder-title', label: t('composeNameLabel'), value: prefill.title, placeholder: t('tasksAddPlaceholder') });
    const worthF = field({ id: 'builder-minutes', label: t('composeWorth'), type: 'number', value: String(prefill.minutes || 5), suffix: t('composeMinSuffix'), attrs: { min: '1' } });

    let refs: EligibilityRefsData = { memberIds: [], tagIds: [] };
    const picker = eligibilityPicker({
      options: eligibilityOptions,
      selected: refs,
      labels: {
        everyone: t('eligEveryone'),
        typePlaceholder: t('eligType'),
        groupTag: t('eligGroupTag'),
        noMatches: t('eligNoMatches'),
        remove: t('eligRemove'),
      },
      onChange: (next) => { refs = next; },
    });

    const visSel = ce('md-outlined-select', { id: 'builder-visibility', 'aria-label': t('composeVisibility') },
      ...([
        ['everyone', 'tasksVisibilityEveryone'],
        ['assignee', 'tasksVisibilityAssignee'],
        ['admins', 'tasksVisibilityAdmins'],
      ] as [string, MessageKey][]).map(([value, label]) =>
        ce('md-select-option', { value, ...(value === 'everyone' ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, t(label))),
      ),
    ) as ValueEl;

    const dueF = field({ id: 'builder-due', label: t('tasksModalDue'), type: 'date' });

    // Labels are a multi-choice and `md-outlined-select` has no multiple mode —
    // filter chips are M3's answer, and the Templates view already uses them
    // for this same field.
    const labelChips = categoryTags.map((tg) =>
      ce('md-filter-chip', { label: tg.name, 'data-tag': String(tg.id) }) as ChipEl,
    );
    const tagsHost: HTMLElement = categoryTags.length
      ? ce('md-chip-set', { id: 'builder-tags' }, ...labelChips)
      : el('span', { class: 'muted', id: 'builder-tags' }, t('tasksNoLabels'));

    const showAtF = field({ id: 'builder-show-at', label: t('tasksModalShowAt'), type: 'time' });
    const hideByF = field({ id: 'builder-hide-by', label: t('tasksModalHideBy'), type: 'time' });

    // Follow-up chains (attach to a saved template, ADR-0040 phase 2).
    const followups: ComposedFollowup[] = [];
    const followupHost = el('div', { class: 'composer-fu-list' });
    function renderFollowups(): void {
      clear(followupHost);
      followups.forEach((f, idx) => {
        const current = f.templateId ? String(f.templateId) : '';
        const sel = ce('md-outlined-select', { 'aria-label': t('composeFollowupTemplate'), class: 'composer-fu-select' },
          ...[['', t('composeFollowupTemplate')] as [string, string], ...templateOptions.map((tpl): [string, string] => [String(tpl.id), tpl.title])].map(([value, label]) =>
            ce('md-select-option', { value, ...(value === current ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, label)),
          ),
        ) as ValueEl;
        sel.addEventListener('change', () => { f.templateId = Number(sel.value) || 0; });
        const delay = ce('md-outlined-text-field', {
          type: 'number', min: '0', value: String(f.delayMinutes),
          'aria-label': t('composeFollowupAfter'), class: 'composer-fu-delay',
        }) as ValueEl;
        delay.addEventListener('input', () => { f.delayMinutes = Number(delay.value) || 0; });
        const removeBtn = ce('md-icon-button', { type: 'button', 'aria-label': t('composeFollowupRemove') }, icons.close());
        removeBtn.addEventListener('click', () => { followups.splice(idx, 1); renderFollowups(); });
        followupHost.append(
          el('div', { class: 'composer-fu' },
            el('div', { class: 'composer-fu-r1' }, sel),
            el('div', { class: 'composer-fu-r2' },
              el('span', { class: 'muted' }, t('composeFollowupAfter')),
              delay,
              el('span', { class: 'muted' }, t('composeMinSuffix')),
              el('span', { class: 'grow' }),
              removeBtn,
            ),
          ),
        );
      });
      const addFollowup = ce('md-text-button', { type: 'button', class: 'composer-fu-add' }, t('composeFollowupAdd'));
      addFollowup.addEventListener('click', () => { followups.push({ templateId: 0, delayMinutes: 0 }); renderFollowups(); });
      followupHost.append(addFollowup);
    }
    renderFollowups();

    // Shared fields for both "Add task" and "Save as template". Returns null
    // (and shows the message inside the sheet) when name or worth is missing.
    function collect(): Record<string, unknown> | null {
      const title = nameF.input.value.trim();
      const baseMinutes = Number(worthF.input.value);
      if (!title || !baseMinutes) { builderError.textContent = t('tasksNameMinutesRequired'); return null; }
      const body: Record<string, unknown> = { title, baseMinutes };
      if (refs.memberIds.length > 0 || refs.tagIds.length > 0) body.eligibilityRefs = refs;
      // '' means Material has not rendered the select yet — treat as default.
      const visibility = visSel.value || 'everyone';
      if (visibility !== 'everyone') body.visibility = visibility;
      const tagIds = categoryTags.filter((_, i) => labelChips[i]!.selected).map((tg) => tg.id);
      if (tagIds.length > 0) body.tagIds = tagIds;
      if (showAtF.input.value) body.showAtTime = showAtF.input.value;
      if (hideByF.input.value) body.hideByTime = hideByF.input.value;
      return body;
    }

    // Both actions: a SUCCESS closes the builder AND the sheet under it and
    // lands on the list; a FAILURE keeps the builder open with the message
    // inside it (ADR-0047).
    function submitTask(): void {
      const body = collect();
      if (!body) return;
      if (dueF.input.value) body.dueAt = new Date(`${dueF.input.value}T23:59:59`).toISOString();
      error.textContent = '';
      api.post<{ pending?: boolean }>('/api/tasks', body)
        .then((res) => {
          builder.close();
          quickAdd.close();
          if (res.pending) error.textContent = t('sentForApproval');
          reloadAll();
        })
        .catch((err: Error) => { builderError.textContent = err.message; });
    }
    function saveTemplate(): void {
      const body = collect();
      if (!body) return;
      api.post('/api/templates', { ...body, followups: followups.filter((f) => f.templateId > 0) })
        .then(() => { builder.close(); quickAdd.close(); reloadAll(); })
        .catch((err: Error) => { builderError.textContent = err.message; });
    }

    const builderAddBtn = ce('md-filled-button', { type: 'button', id: 'builder-add' }, t('composeAddTask'));
    builderAddBtn.addEventListener('click', submitTask);
    const builderSaveTemplateBtn = ce('md-outlined-button', { type: 'button', id: 'builder-save-template' }, t('composeSaveTemplate'));
    builderSaveTemplateBtn.addEventListener('click', saveTemplate);

    clear(builder.body);
    builder.body.append(
      builderError,
      nameF.root,
      worthF.root,
      builderGroup(t('builderAccess'), true, fieldBlock(t('composeWhoCanDo'), picker), fieldBlock(t('composeVisibility'), visSel)),
      builderGroup(t('tasksModalDue'), false, dueF.root),
      builderGroup(t('tasksModalLabels'), false, tagsHost),
      builderGroup(t('builderTiming'), false, showAtF.root, hideByF.root),
      builderGroup(t('composeFollowupHeading'), false, followupHost),
      el('div', { class: 'row builder-actions' }, builderAddBtn, builderSaveTemplateBtn),
      el('p', { class: 'muted builder-repeat-note' }, t('builderRepeatNote')),
    );
    builder.open();
  }

  // Recently closed: done, not-needed, cancelled — and expired (ADR-0025):
  // a day the task just didn't happen stays visible instead of vanishing.
  const historyCard = el('div', { class: 'card', id: 'task-history' });
  historyCard.style.display = 'none';
  const STATUS_LABEL: Record<ClosedTask['status'], MessageKey> = {
    done: 'statusDone',
    skipped: 'statusNotNeeded',
    expired: 'statusExpired',
    cancelled: 'statusCancelled',
  };
  function closedAtOf(t: ClosedTask): string | null {
    return t.completedAt ?? t.skippedAt ?? t.expiredAt;
  }
  function loadHistory(): void {
    api.get<{ tasks: ClosedTask[] }>('/api/tasks/history').then(({ tasks }) => {
      if (tasks.length === 0) {
        historyCard.style.display = 'none';
        return;
      }
      historyCard.style.display = '';
      clear(historyCard);
      historyCard.append(el('h2', {}, t('tasksHistoryTitle')));
      for (const ct of tasks.slice(0, 10)) {
        const when = closedAtOf(ct);
        historyCard.append(
          el(
            'div',
            { class: 'task', 'data-closed-task': ct.title },
            el(
              'div',
              {},
              el('div', { class: 'title muted' }, ct.title),
              el('div', { class: 'meta' }, when ? new Date(when).toLocaleDateString(getLocale()) : ''),
            ),
            el('span', { class: ct.status === 'expired' ? 'pill expired' : 'pill' }, t(STATUS_LABEL[ct.status])),
          ),
        );
      }
    });
  }

  // The tasks screen is a LIST (ADR-0047): heading, status line, open tasks
  // or the empty state, recently-closed history — and a FAB. No authoring
  // form in the page body; that lives in the quick-add sheet.
  root.append(
    error,
    list,
    historyCard,
    quickAdd.root,
    builder.root,
    fab({ label: t('tasksAddTitle'), onClick: () => quickAdd.open() }),
  );
  load();
  loadHistory();
  loadComposer();
  return root;
}
